import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { getAllUsersNoSales, sendUserNotification } from "../../../utilities";
import { setPostLoginRedirect } from "../../../utils/postLoginRedirect";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { LoadingState } from "../../../components/UI/States";

const COMPANY_VALUES = [
  {
    name: "Integrity",
    description:
      "People should trust that we do what we say we'll do and we'll do the right thing.",
  },
  {
    name: "Urgency",
    description:
      "We will maintain a high sense of urgency in dealing with customers.",
  },
  {
    name: "Safety",
    description:
      "We will do our work safely. The safety of our employees, customers, and the public will always supersede the need for expedience.",
  },
  {
    name: "Profitability",
    description:
      "We will charge a fair price for our products and services and place the value of long-term relationships above short-term profit.",
  },
  {
    name: "Respect",
    description:
      "We will show respect towards fellow employees, suppliers, and especially customers.",
  },
  {
    name: "Fun",
    description:
      "We will endeavor to enjoy the job we do and have some fun every day.",
  },
  {
    name: "Community",
    description:
      "We will support the communities where we earn our living with our time and contributions.",
  },
  {
    name: "Advocate",
    description:
      "We will serve as an advocate for our customers by finding the answers to their questions, recommending alternative products, and helping them become more profitable.",
  },
  {
    name: "Relationships",
    description:
      "We will befriend those customers and suppliers who will welcome our friendship.",
  },
  {
    name: "Impression",
    description:
      "We will present ourselves in a way that inspires confidence in every situation where we interact with the public.",
  },
  {
    name: "Salesmanship",
    description:
      "We won't be afraid to ask for the sale, but we will refrain from using negotiating tactics to close a sale.",
  },
  {
    name: "Teamwork",
    description: "Cooperation and support between employees is essential.",
  },
  {
    name: "Caring",
    description:
      "We will operate under the golden rule, treating all people as we want to be treated.",
  },
];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SectionLabel = ({ children, hint, required }) => (
  <div>
    <p className="text-sm font-semibold">
      {children}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </p>
    {hint && <p className="app-muted mt-0.5 text-xs">{hint}</p>}
  </div>
);

const Section = ({ children }) => (
  <section className="app-card space-y-3 rounded-2xl border p-5">
    {children}
  </section>
);

// The mobile route renders without the app header/wrapper, so it brings its
// own full-page surface. Top-level (not defined in render) so the form's
// inputs keep focus between keystrokes.
const Shell = ({ mobile, children }) =>
  mobile ? (
    <div className="app-page min-h-screen w-full">
      <PageShell size="sm">{children}</PageShell>
    </div>
  ) : (
    <PageShell size="sm">{children}</PageShell>
  );

const RecognitionForm = () => {
  const { name, mobile } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [team, setTeam] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [values, setValues] = useState([]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [explanation, setExplanation] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // On the mobile route the submitter's name comes from the URL; on the web
  // it comes from the signed-in user.
  const submitUser = mobile ? name : user?.name;

  useEffect(() => {
    if (!mobile && !user?.name) {
      setPostLoginRedirect(
        window.location.pathname + window.location.search + window.location.hash
      );
      navigate("/inituser");
    }
  }, [user, mobile, navigate]);

  useEffect(() => {
    getAllUsersNoSales(setTeam);
  }, []);

  // The user list prepends an "Other" pseudo-entry — free typing covers that
  // now, so drop it from the options.
  const teamOptions = useMemo(
    () => (team || []).filter((t) => t.label !== "Other"),
    [team]
  );

  const toggleValue = (value) => {
    setValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const addOtherValue = () => {
    const trimmed = otherValue.trim().replace(/,/g, " ");
    if (trimmed && !values.includes(trimmed)) setValues([...values, trimmed]);
    setOtherValue("");
    setOtherOpen(false);
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  // Autocomplete (freeSolo) yields a mix of option objects and typed strings.
  const normalizedEmployees = employees.map((entry) =>
    typeof entry === "string"
      ? { name: entry.trim(), email: null }
      : { name: entry.label, email: entry.email || null }
  );

  const canSubmit =
    normalizedEmployees.length > 0 &&
    values.length > 0 &&
    explanation.trim().length > 0 &&
    !submitting;

  const resetForm = () => {
    setEmployees([]);
    setValues([]);
    setExplanation("");
    setFiles([]);
    setError(null);
    setSubmitted(false);
  };

  const submitForm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const payload = new FormData();
    payload.append("employees", JSON.stringify(normalizedEmployees));
    // Legacy fields — the entries dashboard and email templates read these.
    payload.append(
      "employee",
      normalizedEmployees.map((e) => e.name).join(", ")
    );
    payload.append(
      "email",
      normalizedEmployees
        .map((e) => e.email)
        .filter(Boolean)
        .join(", ")
    );
    payload.append("value", values.join(","));
    payload.append("explanation", explanation.trim());
    payload.append("submit_user", submitUser);
    files.forEach((file) => payload.append("files[]", file));

    try {
      const res = await fetch("/api/forms/submit/recognitionForm", {
        method: "POST",
        body: payload,
      });
      const content = await res.json();
      if (content.success === 1) {
        // In-app pat on the back for everyone picked from the directory
        // (free-typed names have no email to notify).
        normalizedEmployees
          .filter((employee) => employee.email)
          .forEach((employee) =>
            sendUserNotification(
              employee.email,
              submitUser,
              `${submitUser} caught you in the act: ${values.join(", ")}.`,
              "You were Caught in the Act!"
            )
          );
        setSubmitted(true);
      } else {
        setError(content.message || "Something went wrong — please try again.");
      }
    } catch (err) {
      setError("Could not submit the form — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!submitUser) {
    return (
      <Shell mobile={mobile}>
        <LoadingState label="Loading User Data..." className="py-24" />
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell mobile={mobile}>
        <div className="app-card mt-6 flex flex-col items-center rounded-2xl border px-6 py-10 text-center sm:px-10">
          <span className="app-icon-tile h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <TaskAltIcon fontSize="large" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            Recognition submitted!
          </h1>
          <p className="app-muted mt-2 max-w-md text-sm leading-relaxed">
            Thanks for taking the time to celebrate{" "}
            {normalizedEmployees.length > 1 ? "your coworkers" : "a coworker"} —
            they'll be notified by email.
          </p>
          <Button variant="contained" className="!mt-6" onClick={resetForm}>
            Submit Another
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell mobile={mobile}>
      <PageHeader
        icon={LocalActivityIcon}
        tone="emerald"
        eyebrow={mobile ? undefined : "Forms"}
        title="'C'aught in the Act"
        description="Recognize coworkers for demonstrating the Big C values."
      />

      <div className="space-y-5">
        {/* Employees */}
        <Section>
          <SectionLabel
            required
            hint="Pick from the list, or type a name and press Enter to add someone who isn't on it."
          >
            Who are you recognizing?
          </SectionLabel>
          <Autocomplete
            multiple
            freeSolo
            disablePortal
            options={teamOptions}
            value={employees}
            loading={team === null}
            filterSelectedOptions
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, value) =>
              option.label ===
              (typeof value === "string" ? value : value.label)
            }
            onChange={(e, newValue) => setEmployees(newValue)}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={typeof option === "string" ? option : option.label}
                  size="small"
                  color="primary"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  employees.length === 0 ? "Employee name(s)..." : ""
                }
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Who are you recognizing?",
                }}
              />
            )}
          />
        </Section>

        {/* Values */}
        <Section>
          <SectionLabel required hint="Select every value they demonstrated.">
            Which values did they demonstrate?
          </SectionLabel>
          <div className="flex flex-wrap gap-2">
            {COMPANY_VALUES.map((value) => {
              const selected = values.includes(value.name);
              return (
                <Tooltip
                  key={value.name}
                  title={value.description}
                  arrow
                  placement="top"
                  disableInteractive
                  enterDelay={200}
                  enterTouchDelay={300}
                >
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleValue(value.name)}
                    className={`min-h-[2.5rem] rounded-full border px-4 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                      selected
                        ? "border-transparent bg-sky-600 text-white shadow-sm"
                        : "app-surface text-[color:var(--app-text)] hover:border-sky-400"
                    }`}
                  >
                    {value.name}
                  </button>
                </Tooltip>
              );
            })}
            {/* Custom values the user added show up as removable chips */}
            {values
              .filter((v) => !COMPANY_VALUES.some((cv) => cv.name === v))
              .map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed="true"
                  onClick={() => toggleValue(value)}
                  className="min-h-[2.5rem] rounded-full border border-transparent bg-sky-600 px-4 text-sm font-medium text-white shadow-sm transition-colors"
                >
                  {value} ✕
                </button>
              ))}
            {otherOpen ? (
              <span className="inline-flex items-center gap-1">
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Custom value"
                  value={otherValue}
                  onChange={(e) => setOtherValue(e.target.value)}
                  inputProps={{ "aria-label": "Custom value" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOtherValue();
                    }
                    if (e.key === "Escape") setOtherOpen(false);
                  }}
                  sx={{ width: 160 }}
                />
                <Button size="small" onClick={addOtherValue}>
                  Add
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setOtherOpen(true)}
                className="app-muted inline-flex min-h-[2.5rem] items-center gap-1 rounded-full border border-dashed border-[color:var(--app-input-border)] px-4 text-sm font-medium transition-colors hover:border-sky-400 hover:text-[color:var(--app-text)]"
              >
                <AddIcon sx={{ fontSize: 16 }} /> Other
              </button>
            )}
          </div>
        </Section>

        {/* Explanation */}
        <Section>
          <SectionLabel
            required
            hint="What did they do? This is shared with them and the exec team."
          >
            Tell the story
          </SectionLabel>
          <TextField
            fullWidth
            multiline
            minRows={5}
            placeholder="Describe what happened and why it deserves recognition..."
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            inputProps={{ "aria-label": "Tell the story" }}
          />
        </Section>

        {/* Files */}
        <Section>
          <SectionLabel hint="Optional — attach photos or files that go with the story.">
            Attachments
          </SectionLabel>
          <Button
            component="label"
            variant="outlined"
            startIcon={<AttachFileIcon />}
          >
            Add Files
            <input type="file" hidden multiple onChange={handleFileChange} />
          </Button>
          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="app-surface flex items-center gap-2 rounded-xl border py-1 pl-3 pr-1 text-sm"
                >
                  <InsertDriveFileIcon
                    className="app-muted shrink-0"
                    sx={{ fontSize: 18 }}
                  />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="app-muted shrink-0 text-xs tabular-nums">
                    {formatFileSize(file.size)}
                  </span>
                  <IconButton
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      setFiles(files.filter((_, i) => i !== index))
                    }
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {error && (
          <div
            role="alert"
            className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
          >
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="app-card flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="app-muted text-sm">
              Submitting as <strong>{submitUser}</strong>
            </p>
            {!canSubmit && !submitting && (
              <p className="app-muted text-xs">
                Pick someone, choose a value, and tell the story to submit.
              </p>
            )}
          </div>
          <Button
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={submitForm}
            className="w-full sm:w-auto"
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {submitting ? "Submitting..." : "Submit Recognition"}
          </Button>
        </div>
      </div>
    </Shell>
  );
};

export default RecognitionForm;
